import React, { FocusEventHandler } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteProps,
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import { FormikProps } from 'formik';
import { get } from 'lodash';

export type Overwrite<T, U> = Pick<T, Exclude<keyof T, keyof U>> & U;

/**
 * TextField and Autocomplete have conflicting onChange type definitions.
 * Overwrite TextFieldProps with onChange from Autocomplete
 *
 * @see https://stackoverflow.com/questions/43080547/how-to-override-type-properties-in-typescript?rq=1
 */
type AutocompletePropsWithTextField<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> = Overwrite<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  Pick<
    TextFieldProps,
    | 'name'
    | 'color'
    | 'defaultValue'
    | 'variant'
    | 'error'
    | 'helperText'
    | 'label'
    | 'margin'
    | 'multiline'
    | 'placeholder'
    | 'required'
    | 'type'
  > & {
    // /** Change handler for Autocomplete triggered when dropdown value changed */
    // onChange?: (
    //   event: React.ChangeEvent<any>,
    //   value: any,
    //   reason: AutocompleteChangeReason,
    //   details?: AutocompleteChangeDetails<any>
    // ) => void;
    /**
     * Render the input. (override required with optional renderInput)
     *
     * @param {object} params
     * @returns {ReactNode}
     */
    renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  }
>;

/**
 * Complete TextFieldProps with Autocomplete
 */
interface IMaterialAutocomplete<T> {
  /** Formik object */
  formik?: FormikProps<T>;
  additionalTextFieldProps?: TextFieldProps;
}

/**
 * MaterialForm wrapper over Autocomplete over TextField
 *
 * @description custom onChange type overwrites TextFieldProps
 *
 * @param param Parameters accepted by TextField
 */
export const MaterialAutocomplete = <
  TFormik,
  TAutocomplete,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>({
  className,
  formik,
  value,
  onChange,
  onBlur,
  options,
  additionalTextFieldProps,

  //#region TextFieldProps
  name,
  color,
  defaultValue,
  defaultChecked,
  variant,
  error,
  helperText,
  id,
  label,
  margin,
  multiline,
  placeholder,
  required,
  type,
  dataPup,
  //#endregion TextFieldProps

  ...rest
}: IMaterialAutocomplete<TFormik> &
  AutocompletePropsWithTextField<
    TAutocomplete,
    Multiple,
    DisableClearable,
    FreeSolo
  > & {
    dataPup?: string;
  }) => {
  const formikProps = name && formik ? formik.getFieldProps(name) : undefined;
  // set internal state if not using external state
  const [state, setState] = React.useState<any>(defaultValue);

  /**
   * Chain onChange to caller onChange
   * @param event HTML change event
   */
  const onChangeHandler = (
    event: React.ChangeEvent<any>,
    value: any,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any>
  ) => {
    if (onChange) {
      onChange(event, value, reason, details);
    }
    if (formikProps) {
      // forward event to formik for proper error handling and value setting
      formikProps.onChange({
        target: {
          name,
          value: value ?? '',
        },
      });
    } else {
      setState(value);
    }
  };

  return (
    <Autocomplete
      {...rest}
      options={options}
      data-pup={dataPup}
      className={className}
      value={
        formikProps?.value || value || state // || undefined // auto-complete needs null not '' or undefined
      }
      onChange={onChangeHandler}
      renderInput={(props) => (
        <TextField
          {...additionalTextFieldProps}
          {...props}
          name={formikProps?.name ?? name}
          onBlur={
            onBlur as
              | FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
              | undefined
          }
          color={color}
          defaultChecked={defaultChecked}
          variant={variant}
          label={label}
          margin={margin}
          multiline={multiline}
          placeholder={placeholder}
          required={required}
          type={type}
          error={Boolean(
            (formik &&
              name &&
              get(formik.touched, name) &&
              get(formik.errors, name)) ||
              error
          )}
          helperText={
            (formik &&
              name &&
              get(formik.touched, name) &&
              get(formik.errors, name)) ||
            helperText
          }
        />
      )}
    />
  );
};
